/**
 * @fileoverview General utility functions for date pickers, validation, graph conversion, and URL management.
 * Provides reusable utilities for NRIC/work permit validation, placeholder images, and date range shortcuts.
 */

import _ from "lodash";
import qs from "query-string";
import { GraphNode } from "./type";
import moment from "moment";

const shortcuts = [
  {
    text: "Today",
    onClick(picker: any) {
      const end = new Date();
      const start = new Date();
      picker.$emit("pick", [start, end]);
    },
  },
  {
    text: "Yesterday",
    onClick(picker: any) {
      const end = new Date();
      const start = new Date();
      start.setTime(start.getTime() - 3600 * 1000 * 24);
      picker.$emit("pick", [start, end]);
    },
  },
  {
    text: "Last week",
    onClick(picker: any) {
      const end = new Date();
      const start = new Date();
      start.setTime(start.getTime() - 3600 * 1000 * 24 * 7);
      picker.$emit("pick", [start, end]);
    },
  },
  {
    text: "Last month",
    onClick(picker: any) {
      const end = new Date();
      const start = new Date();
      start.setTime(start.getTime() - 3600 * 1000 * 24 * 30);
      picker.$emit("pick", [start, end]);
    },
  },
  {
    text: "Last 3 months",
    onClick(picker: any) {
      const end = new Date();
      const start = new Date();
      start.setTime(start.getTime() - 3600 * 1000 * 24 * 90);
      picker.$emit("pick", [start, end]);
    },
  },
  {
    text: "Last 6 months",
    onClick(picker: any) {
      const end = new Date();
      const start = new Date();
      start.setTime(start.getTime() - 3600 * 1000 * 24 * 180);
      picker.$emit("pick", [start, end]);
    },
  },
  {
    text: "Last year",
    onClick(picker: any) {
      const end = new Date();
      const start = new Date();
      start.setTime(start.getTime() - 3600 * 1000 * 24 * 365);
      picker.$emit("pick", [start, end]);
    },
  },
];
const firstSetOfShorcuts = [
  {
    text: "Tomorrow",
    onClick(picker: any) {
      const date = new Date();
      date.setTime(date.getTime() + 3600 * 1000 * 24);
      picker.$emit("pick", date);
    },
  },
  {
    text: "A week later",
    onClick(picker: any) {
      const date = new Date();
      date.setTime(date.getTime() + 3600 * 1000 * 24 * 7);
      picker.$emit("pick", date);
    },
  },
  {
    text: "A month later",
    onClick(picker: any) {
      const date = new Date();
      date.setTime(date.getTime() + 3600 * 1000 * 24 * 30);
      picker.$emit("pick", date);
    },
  },
];
const secondSetOfShortcuts = [
  {
    text: "Tomorrow",
    onClick(picker: any) {
      const today = moment();
      const date = today.add(1, "day").toDate();
      picker.$emit("pick", date);
    },
  },
  {
    text: "A week later",
    onClick(picker: any) {
      const today = moment();
      const date = today.add(1, "week").toDate();
      picker.$emit("pick", date);
    },
  },
  {
    text: "A month later",
    onClick(picker: any) {
      const today = moment();
      const date = today.add(1, "month").toDate();
      picker.$emit("pick", date);
    },
  },
];

/**
 * Date picker options that only allow future dates.
 * Includes shortcuts for tomorrow, next week, and next month.
 */
export const pickerOptionsForFutureDatesOnly = {
  disabledDate(time: Date) {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    return time.getTime() < yesterday.getTime();
  },
  shortcuts: firstSetOfShorcuts,
};

/**
 * Alternative date picker options for future dates using moment.js.
 * Includes shortcuts for tomorrow, next week, and next month.
 */
export const pickerOptionsForFutureDatesVersionII = {
  disabledDate(time: Date) {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    return time.getTime() < yesterday.getTime();
  },
  shortcuts: secondSetOfShortcuts,
};

/**
 * Default date range picker options.
 * Includes shortcuts for today, yesterday, last week/month/year, etc.
 */
export const defaultPickerOptions = {
  shortcuts,
};

type ApolloErrorCode = "FORBIDDEN" | "UNAUTHENTICATED" | "GRAPHQL_VALIDATION_FAILED";

/**
 * Converts Apollo error codes to user-friendly messages.
 *
 * @param {ApolloErrorCode} errorCode - Apollo GraphQL error code
 * @param {string} message - Original error message
 * @returns {string} User-friendly error message
 *
 * @example
 * getMessageByCode('FORBIDDEN', 'Access denied'); // '401: User does not have permissions'
 */
export function getMessageByCode(errorCode: ApolloErrorCode, message: string): string {
  let finalMessage = message;
  switch (errorCode) {
    case "FORBIDDEN":
      finalMessage = "401: User does not have permissions";
      break;
    case "UNAUTHENTICATED":
      finalMessage = "401: User is not authenticated";
      break;
    case "GRAPHQL_VALIDATION_FAILED":
      finalMessage = "Encountered GRAPHQL_VALIDATION_FAILED error";
      break;
  }
  return finalMessage;
}

/**
 * Validates Singapore work permit number format.
 * Format: 8 digits with space at position 1, last char can be digit or dash.
 *
 * @param {string} workPermit - Work permit number to validate
 * @returns {boolean} True if valid format
 *
 * @example
 * isWorkPermitValid('1 2345678'); // true
 * isWorkPermitValid('12345678-'); // true
 */
export function isWorkPermitValid(workPermit: string): boolean {
  if (!workPermit || workPermit.length != 10) return false;
  const digits = "1234567890".split("");

  for (let i = 0; i < 10; i++) {
    const char = workPermit.charAt(i);

    // second character must be whitespace, if not return false
    if (i === 1) {
      if (char !== " ") return false;
    } else if (i === 9) {
      const lastCharCheck = digits.includes(char) || char === "-";
      if (!lastCharCheck) return false;
    } else if (!digits.includes(char)) {
      return false;
    }
  }
  return true;
}

/**
 * Validates Singapore NRIC/FIN using official checksum algorithm.
 * Supports S/T series (NRIC), F/G series (FIN), and M series (FIN).
 *
 * @param {string} nric - NRIC/FIN number to validate
 * @returns {boolean} True if valid NRIC/FIN
 *
 * @example
 * isNricValid('S1234567D'); // true (if checksum matches)
 * isNricValid('F1234567N'); // true (if checksum matches)
 */
export function isNricValid(nric: string): boolean {
  const isValidLength = nric?.length === 9;
  const firstLetter = nric ? nric[0]?.toUpperCase() : "";
  const isValidFirstLetter = ["S", "T", "G", "F", "M"].includes(firstLetter);

  if (!isValidLength || !isValidFirstLetter) {
    return false;
  }

  const lastLetter = nric[8].toUpperCase();
  const nricCheckDigits = "JZIHGFEDCBA";
  const finCheckDigits = "XWUTRQPNMLK";
  const mFinCheckDigits = "KLJNPQRTUWX";
  const digits = nric.slice(1, 8);
  const weights = [2, 7, 6, 5, 4, 3, 2];
  let sum = 0;

  if (isNaN(+digits)) {
    return false;
  }
  if (["T", "G"].includes(firstLetter)) {
    sum += 4;
  }
  for (let i = 0; i < digits.length; i++) {
    sum += parseInt(digits.charAt(i), 10) * weights[i];
  }
  if (["S", "T"].includes(firstLetter)) {
    return nricCheckDigits.charAt(sum % 11) === lastLetter;
  }
  // m-series FIN
  if (firstLetter === "M") {
    // 1. increase sum by the weightage (3)
    sum += 3;
    // 2. calculate remainder, R1 = remainder of sum / 11
    const remainder = sum % 11;
    // 3. calculate check digit number, P = 11 - R1
    const checkDigit = 11 - remainder;
    // 4. minus P by 1, because M-series digit mapping formula is started by index 1
    const checkDigitIndex = checkDigit - 1;

    return mFinCheckDigits.charAt(checkDigitIndex) === lastLetter;
  }
  return finCheckDigits.charAt(sum % 11) === lastLetter;
}

/**
 * Converts content nodes to G6 graph format for visualization.
 * Creates nodes and edges for first 10 nodes only.
 *
 * @param {GraphNode} nodes - Content nodes object indexed by node ID
 * @returns {{nodes: Array, edges: Array}} G6-formatted graph data
 *
 * @example
 * const graphData = convertNodesToG6(contentNodes);
 */
export function convertNodesToG6(nodes: GraphNode) {
  if (!_.isObject(nodes)) {
    return [];
  }

  const result: any = { nodes: [], edges: [] };
  const sortedKeys = _.keys(nodes);
  const slicedKeys = _.slice(sortedKeys, 0, 10);

  let x = 200,
    y = 100,
    i = 0;

  _.forEach(slicedKeys, (key: string) => {
    const node: any = nodes[key];
    const edges = getEdges(node);
    const next = edges[0];

    const nodeObject = {
      id: key,
      x,
      y,
      label: key !== "conversation_start" && key !== "conversation_end" ? key : "",
      clazz:
        key === "conversation_start"
          ? "startKr"
          : key === "conversation_end"
          ? "endKr"
          : "basicStateKr",
    };

    result.nodes.push(nodeObject);

    if (node.next) {
      const edge = {
        source: key,
        target: node.next,
        sourceAnchor: 1,
        targetAnchor: 0,
        clazz: "flow",
      };
      result.edges.push(edge);
    }

    // check every 10th row, it will create increment x and reset y
    if (i % 10 === 0 && i !== 0) {
      x = x + 300;
      y = 0;
    }

    // increment y
    y = y + 150;

    i++;
  });

  return result;
}

/**
 * Extracts edge connections from a content node.
 *
 * @param {object} node - Content node to extract edges from
 * @returns {Array} Array of edge target IDs
 */
function getEdges(node: object) {
  const edges: any[] = [];

  edges.push(_.get(node, "next"));

  edges.push(_.get(node, "content.buttons.event"));
  edges.push(_.get(node, "content.buttons.data"));
  edges.push(_.get(node, "quickReplies.event"));
  edges.push(_.get(node, "quickReplies.data"));

  return edges;
}

/**
 * Checks if a value is a function.
 *
 * @param {any} fn - Value to check
 * @returns {boolean} True if value is a function
 *
 * @example
 * isFunction(() => {}); // true
 * isFunction('test'); // false
 */
export function isFunction(fn: any) {
  return typeof fn === "function";
}

/**
 * Generates a placeholder image URL using placehold.co service.
 *
 * @param {number} width - Image width in pixels
 * @param {number} height - Image height in pixels
 * @param {string} text - Text to display on placeholder
 * @returns {string} Placeholder image URL
 *
 * @example
 * getPlaceholderImage(300, 200, 'Logo'); // 'https://placehold.co/300x200?text=Logo'
 */
export function getPlaceholderImage(width: number, height: number, text: string) {
  const imageText = text.replace(/\s/g, "+");
  return `https://placehold.co/${width}x${height}?text=${imageText}`;
}

/**
 * Gets the default placeholder image for cards (310x160).
 *
 * @returns {string} Default placeholder image URL
 *
 * @example
 * const imgUrl = getDefaultPlaceholderImage();
 */
export function getDefaultPlaceholderImage() {
  return getPlaceholderImage(310, 160, "Add Image");
}

/**
 * Checks if hostname is localhost (0.0.0.0, 127.0.0.*, localhost).
 *
 * @returns {boolean} True if local hostname
 */
const isLocal = () => /^(0.0.0.0|127.0.0.*|localhost)$/.test(location.hostname);

/**
 * Checks if hostname is in local network range (192.168.*.*).
 *
 * @returns {boolean} True if local network
 */
const isLocalNetwork = () => /^(192.168.\d+.\d+)$/.test(location.hostname);

/**
 * Checks if hostname is localhost or local network.
 *
 * @returns {boolean} True if local environment
 *
 * @example
 * if (isLocalhost()) {
 *   console.log('Running locally');
 * }
 */
const isLocalhost = () => {
  return isLocal() || isLocalNetwork();
};

export { isLocal, isLocalhost, isLocalNetwork };

/**
 * Formats an authentication token with Bearer prefix.
 *
 * @param {string} [token=''] - Authentication token
 * @returns {string} Formatted Bearer token or empty string
 *
 * @example
 * formatToken('abc123'); // 'Bearer abc123'
 */
export const formatToken = (token = "") => {
  if (token) {
    return `Bearer ${token}`;
  } else {
    return "";
  }
};

/**
 * Updates the browser URL query string without reloading the page.
 * Uses HTML5 History API to push new state.
 *
 * @param {Object} query - Query parameters object
 *
 * @example
 * updateUrlQuery({ tab: 'settings', page: 2 });
 */
export const updateUrlQuery = (query) => {
  if (window.history.pushState) {
    const validQuery = _.pickBy(query, _.identity);
    const newurl = window.location.href.split("?")[0] + "?" + qs.stringify(validQuery);
    window.history.pushState({ path: newurl }, "", newurl);
  }
};
